import type { TerminalLine } from './Terminal'
import { terminalConfig } from '../config/terminal.config'

/**
 * 解析 navigator.userAgent 提取浏览器名 + 版本。
 */
const getBrowserInfo = (): string => {
	if (typeof navigator === 'undefined') return 'Unknown'
	const ua = navigator.userAgent
	const edgeMatch = ua.match(/Edg\/(\d+(?:\.\d+)*)/)
	if (edgeMatch) return `Edge ${edgeMatch[1]}`
	const chromeMatch = ua.match(/Chrome\/(\d+(?:\.\d+)*)/)
	if (chromeMatch) return `Chrome ${chromeMatch[1]}`
	const firefoxMatch = ua.match(/Firefox\/(\d+(?:\.\d+)*)/)
	if (firefoxMatch) return `Firefox ${firefoxMatch[1]}`
	const safariMatch = ua.match(/Version\/(\d+(?:\.\d+)*).*Safari/)
	if (safariMatch && !/Chrome/.test(ua)) return `Safari ${safariMatch[1]}`
	return 'Unknown'
}

const getPlatform = (): string => {
	if (typeof navigator === 'undefined') return 'Unknown'
	const ua = navigator.userAgent
	const ntMatch = ua.match(/Windows NT (\d+\.\d+)/)
	if (ntMatch) {
		const map: Record<string, string> = {
			'10.0': 'Windows 10/11',
			'6.3': 'Windows 8.1',
			'6.2': 'Windows 8',
			'6.1': 'Windows 7'
		}
		return map[ntMatch[1]] || `Windows NT ${ntMatch[1]}`
	}
	if (/Mac OS X/.test(ua)) return 'macOS'
	if (/Android/.test(ua)) {
		const androidMatch = ua.match(/Android (\d+(?:\.\d+)*)/)
		return androidMatch ? `Android ${androidMatch[1]}` : 'Android'
	}
	if (/iPhone|iPad|iPod/.test(ua)) {
		const iosMatch = ua.match(/OS (\d+(?:_\d+)*)/)
		return iosMatch ? `iOS ${iosMatch[1].replace(/_/g, '.')}` : 'iOS'
	}
	if (/Linux/.test(ua)) return 'Linux'
	return 'Unknown'
}

const getResolution = (): string => {
	if (typeof window === 'undefined') return 'Unknown'
	return `${window.screen.width}×${window.screen.height}`
}

const getViewport = (): string => {
	if (typeof window === 'undefined') return 'Unknown'
	return `${window.innerWidth}×${window.innerHeight}`
}

const getLanguage = (): string => (typeof navigator === 'undefined' ? 'Unknown' : navigator.language)

const getCores = (): string =>
	typeof navigator === 'undefined' ? 'Unknown' : String(navigator.hardwareConcurrency ?? 'Unknown')

const getMemory = (): string => {
	if (typeof navigator === 'undefined') return 'Unknown'
	const dm = (navigator as unknown as { deviceMemory?: number }).deviceMemory
	return dm ? `${dm} GB` : 'Unknown'
}

const getTime = (): string => new Date().toLocaleString('zh-CN', { hour12: false })

/** 用 config 中的静态 value 填充，未填写的 key 用运行时数据 */
const resolveStaticInfo = (): Array<[string, string, boolean]> => {
	const runtimeMap: Record<string, string> = {
		OS: terminalConfig.version.app,
		Host: terminalConfig.brand.hostname,
		Kernel: terminalConfig.version.kernel,
		Resolution: `${getResolution()} (viewport ${getViewport()})`,
		Browser: getBrowserInfo(),
		Platform: getPlatform(),
		Locale: getLanguage(),
		'CPU Cores': getCores(),
		Memory: getMemory(),
		Date: getTime()
	}
	return terminalConfig.staticInfo.map((row) => {
		const value = row.value || runtimeMap[row.key] || 'Unknown'
		return [row.key, value, !!row.highlight] as [string, string, boolean]
	})
}

/**
 * `uname` 无参数：单行核心信息
 */
const getUnameBasic = (): string => {
	return `${terminalConfig.brand.hostname} ${terminalConfig.version.app} ${getPlatform()} ${getBrowserInfo()} ${getLanguage()}`
}

/**
 * `uname -a`：单行完整信息（POSIX 风格）
 */
const getUnameAll = (): string => {
	return [
		terminalConfig.brand.hostname,
		terminalConfig.version.app,
		`build=${terminalConfig.version.build}`,
		`kernel=${terminalConfig.version.kernel}`,
		`semi=${terminalConfig.version.semi}`,
		getPlatform(),
		`browser=${getBrowserInfo()}`,
		`lang=${getLanguage()}`,
		`resolution=${getResolution()}`,
		`cores=${getCores()}`,
		`memory=${getMemory()}`,
		new Date().toISOString()
	].join(' ')
}

/**
 * `neofetch` 风格：ASCII art logo + 对齐系统信息 + 底部主题色块
 * 内容由 terminal.config.ts 配置 + 运行时数据（browser/platform/time）组合
 */
const getNeofetch = (): TerminalLine[] => {
	const rows = resolveStaticInfo()
	const keyWidth = Math.max(...rows.map(([k]) => k.length))

	const lines: TerminalLine[] = []

	// Logo —— 白色 + 多层 cyan/blue 发光（Neon / HUD 风格）
	// 白色主体在彩色光晕背景下对比度高，text-shadow 强化科技感
	terminalConfig.logo.forEach((line) => {
		lines.push({ type: 'system', text: line, color: '#ffffff' })
	})

	// 标题分隔
	lines.push({ type: 'system', text: '' })
	lines.push({ type: 'system', text: `${terminalConfig.brand.user}@${terminalConfig.brand.hostname}` })
	lines.push({ type: 'system', text: '-'.repeat(keyWidth + 30) })

	// 信息行（关键行用 primary 色突出）
	for (const [k, v, highlight] of rows) {
		lines.push({
			type: 'output',
			text: `${k.padEnd(keyWidth)} : ${v}`,
			color: highlight ? ('primary' as const) : undefined
		})
	}

	// 底部主题色块
	lines.push({ type: 'empty' })
	lines.push({ type: 'system', text: 'Theme palette:' })
	const paletteStr = terminalConfig.palette.map((p) => `${p.block} ${p.label}`).join('  ')
	lines.push({ type: 'output', text: paletteStr, color: 'primary' })

	return lines
}

/**
 * 根据命令参数返回对应的输出行
 */
export const runUname = (args: string[]): TerminalLine[] => {
	const isAll = args.includes('-a') || args.includes('--all')
	const isFetch = args.includes('-f') || args.includes('--fetch')

	if (isFetch || args[0] === 'neofetch') {
		return getNeofetch()
	}
	if (isAll) {
		return [{ type: 'output', text: getUnameAll() }]
	}
	return [{ type: 'output', text: getUnameBasic() }]
}

export {
	getBrowserInfo,
	getPlatform,
	getResolution,
	getViewport,
	getLanguage,
	getCores,
	getMemory,
	getTime,
	getUnameBasic,
	getUnameAll,
	getNeofetch
}
